import { useState } from "react";
import { Loader2, X, KeyRound, ShieldAlert, Check, LogOut } from "lucide-react";
import { useAuth } from "./AuthContext";

/**
 * Change-password modal. Two modes:
 *   forced=true  — non-dismissible (no Cancel / no X), shown when the user
 *                  must change their password (initial owner or admin reset).
 *   forced=false — voluntary; user can cancel.
 *
 * Props: { forced?: boolean, onClose?: () => void, onDone?: () => void }
 */
export default function ChangePasswordModal({ forced = false, onClose, onDone }) {
  const { apiFetch, refreshUser, logout, user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("Passwords don't match."); return; }
    if (next === current) { setError("New password can't be the same as the current one."); return; }
    setSubmitting(true);
    try {
      await apiFetch("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      await refreshUser();
      setDone(true);
      setTimeout(() => { onDone?.(); }, 900);
    } catch (err) {
      setError(err.message || "Couldn't change password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog"
         className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/85 backdrop-blur-sm"
         onClick={(e) => { if (!forced && e.target === e.currentTarget) onClose?.(); }}
         data-testid="change-password-modal">
      <div className="w-full max-w-md bg-[#0E0E0F] border border-[#27272A]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#27272A]">
          <div className="flex items-center gap-3 min-w-0">
            <KeyRound className="w-4 h-4 text-[#10B981] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.25em] font-bold text-[#10B981]">
                {forced ? "PASSWORD CHANGE REQUIRED" : "CHANGE PASSWORD"}
              </div>
              <div className="font-mono text-sm text-white truncate">{user?.email}</div>
            </div>
          </div>
          {!forced && (
            <button onClick={onClose} aria-label="Close"
              data-testid="change-pwd-close-btn"
              className="w-8 h-8 grid place-items-center border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#10B981] transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {forced && (
          <div className="px-5 pt-4 pb-1 flex items-start gap-2 text-[12px] text-[#F59E0B] leading-relaxed bg-[rgba(245,158,11,0.06)] border-b border-[rgba(245,158,11,0.20)]">
            <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span className="pb-3">For security, set a new password before continuing. The temporary one stays in our config file and shouldn't be used long-term.</span>
          </div>
        )}

        {done ? (
          <div className="p-8 text-center" data-testid="change-pwd-success">
            <div className="w-12 h-12 mx-auto grid place-items-center border border-[rgba(16,185,129,0.35)] bg-[rgba(16,185,129,0.08)] text-[#10B981] mb-4">
              <Check className="w-5 h-5" />
            </div>
            <div className="text-sm text-white mb-1">Password updated.</div>
            <div className="text-xs text-[#A1A1AA]">You're all set.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <Field label="Current password">
              <input required type="password" autoFocus
                value={current} onChange={(e) => setCurrent(e.target.value)}
                data-testid="change-pwd-current-input"
                className="w-full bg-black border border-[#27272A] focus:border-[#10B981] outline-none px-4 py-3 text-sm text-white transition-colors" />
            </Field>
            <Field label="New password (min 8 chars)">
              <input required type="password"
                value={next} onChange={(e) => setNext(e.target.value)}
                data-testid="change-pwd-new-input"
                className="w-full bg-black border border-[#27272A] focus:border-[#10B981] outline-none px-4 py-3 text-sm text-white transition-colors" />
            </Field>
            <Field label="Confirm new password">
              <input required type="password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="change-pwd-confirm-input"
                className="w-full bg-black border border-[#27272A] focus:border-[#10B981] outline-none px-4 py-3 text-sm text-white transition-colors" />
            </Field>
            {error && (
              <div className="flex items-start gap-2 text-[12px] text-[#EF4444] leading-relaxed bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.25)] p-3" data-testid="change-pwd-error">
                <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <div className="flex justify-between items-center gap-2 pt-2">
              {forced ? (
                <button type="button" onClick={logout}
                  data-testid="change-pwd-signout-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[#27272A] text-[#A1A1AA] hover:text-[#EF4444] hover:border-[#EF4444] transition-colors">
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              ) : (
                <button type="button" onClick={onClose}
                  data-testid="change-pwd-cancel-btn"
                  className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[#27272A] text-[#A1A1AA] hover:text-white transition-colors">
                  Cancel
                </button>
              )}
              <button type="submit" disabled={submitting}
                data-testid="change-pwd-submit-btn"
                className="inline-flex items-center gap-2 px-5 py-2 text-[10px] tracking-[0.2em] font-bold uppercase bg-[#10B981] hover:bg-[#0e9e6e] disabled:bg-[#27272A] disabled:text-[#52525B] text-black transition-colors">
                {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><Check className="w-3 h-3" /> Update password</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] font-bold text-[#52525B] mb-2">{label}</span>
      {children}
    </label>
  );
}
